import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SiQuickbooks } from "react-icons/si";
import { Switch } from "@/components/ui/switch";
import { useApi, money } from "@/lib/api";
import { useCanEdit, VIEW_ONLY_TIP } from "@/components/app/TrialBanner";

const CAT_COLORS = {
  Payroll: "#00FF9D", Software: "#9D00FF", Marketing: "#FFB800", Infrastructure: "#00C2FF",
  Contractors: "#FF5C7A", "Rent & Office": "#A3A3A3", "Legal & Accounting": "#E0E0E0", Insurance: "#7A7A7A",
};

export const ExpenseList = ({ onChange }) => {
  const api = useApi();
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["expenses"], queryFn: () => api.get("/expenses") });
  const cut = data.filter((e) => !e.active).reduce((s, e) => s + e.amount, 0);

  const toggle = async (e, active) => {
    qc.setQueryData(["expenses"], (old) => old.map((x) => (x.id === e.id ? { ...x, active } : x)));
    try {
      await api.patch(`/expenses/${e.id}`, { active });
      toast(active ? `${e.vendor} restored` : `${e.vendor} cut off — saves ${money(e.amount)}/mo`);
      onChange();
    } catch {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      toast.error("Couldn't update expense");
    }
  };

  return (
    <div data-testid="expense-list" className="rounded-xl border border-[#2A2A2A] bg-[#121212]">
      <div className="p-6 pb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-xl">Expense toggles</h3>
          <p className="text-sm text-[#A3A3A3] mt-1 flex items-center gap-1.5">
            <SiQuickbooks className="text-[#2CA01C]" /> Auto-categorized from QuickBooks. Switch a vendor off to model a cut.
          </p>
        </div>
        {cut > 0 && <p data-testid="expense-savings" className="font-mono text-sm text-[#00FF9D]">−{money(cut)}/mo modeled</p>}
      </div>
      <div className="divide-y divide-[#2A2A2A] border-t border-[#2A2A2A]">
        {data.map((e) => (
          <div key={e.id} data-testid={`expense-row-${e.id}`}
            className={`flex items-center gap-4 px-6 py-3 transition-opacity ${e.active ? "" : "opacity-45"}`}>
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: CAT_COLORS[e.category] || "#A3A3A3" }} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm truncate ${e.active ? "" : "line-through"}`}>{e.vendor}</p>
              <p className="text-[11px] text-[#A3A3A3]">{e.category}</p>
            </div>
            <p className="font-mono text-sm w-24 text-right">{money(e.amount)}</p>
            <Switch data-testid={`expense-toggle-${e.id}`} checked={e.active} onCheckedChange={(v) => toggle(e, v)} disabled={!canEdit} title={canEdit ? undefined : VIEW_ONLY_TIP} style={canEdit ? undefined : { opacity: 0.3, cursor: "not-allowed" }}
              className="data-[state=checked]:bg-[#00FF9D]" />
          </div>
        ))}
      </div>
    </div>
  );
};
